import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { Loading, Button, Box } from "@foris/avocado-suite";
import { linkSearchMutation } from "../graphql/advanceSearch.query";
import useContextUrl from "@common/hooks/useContextUrl";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import AdvancedSelectorsWrapper from "@shared/ui/advance-search/advanced-selectors-wrapper";
import EmptyResultsModal from "@modules/shared/empty-results-modal";
import { LinkFilterInput } from "@models/ISchema";
import css from "./advanceSearch.module.scss";

const AdvanceSearch: React.FC = () => {
  // States for validations
  const [error, setError] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectorsState, setSelectorsState] = useState({
    campus: null,
    department: null,
    modality: null,
    shift: null,
    program: null,
    curriculum: null,
    level: null,
    course: null,
  });
  // Hooks for navigation
  const history = useHistory();
  const [advanceSearchMutation] = useMutation(linkSearchMutation, {});
  const { origin, scenario } = useContextUrl();
  const { advancedSearchResultsUrl } = useNavigationUrl();
  const { t } = useTranslation();

  const hasSomeFilter = useMemo(() => {
    return Object.values(selectorsState).some(value => !!value);
  }, [selectorsState]);

  const createObjectMutation = () => {
    const filterArray = [];
    const programFilters = [];

    selectorsState.level && filterArray.push({ level: { eq: selectorsState.level } });
    selectorsState?.program && programFilters.push({ code: { eq: selectorsState.program } });
    selectorsState.campus && programFilters.push({ campusId: { eq: selectorsState.campus } });
    selectorsState.modality && programFilters.push({ modalityId: { eq: selectorsState.modality } });
    selectorsState.department &&
      programFilters.push({ departmentId: { eq: selectorsState.department } });
    selectorsState.shift && programFilters.push({ shiftId: { eq: selectorsState.shift } });

    if (programFilters.length > 0) {
      filterArray.push({
        curriculum: {
          code: selectorsState?.curriculum ? { eq: selectorsState.curriculum } : undefined,
          program: {
            and: programFilters,
          },
        },
      });
    }

    const filter: LinkFilterInput = {
      fields:
        selectorsState?.course || filterArray?.length
          ? {
              courseId: selectorsState?.course ? { eq: selectorsState.course } : undefined,
              course: filterArray?.length ? { and: filterArray } : undefined,
            }
          : undefined,
      pagination: {
        page: 1,
        size: 10,
      },
    };

    const variables = {
      originId: origin,
      scenarioId: scenario,
      filter,
    };
    return variables;
  };

  const callUpdateHook = async () => {
    setLoading(true);
    try {
      const variables = createObjectMutation();
      const { data } = await advanceSearchMutation({ variables });
      setLoading(false);
      const dataItems = data.filter.createLinkFilter;
      if (dataItems.links.items.length > 0) {
        const idSearch = dataItems.id;
        history.push(advancedSearchResultsUrl("link", idSearch));
      } else {
        setIsEmpty(true);
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
      setError(true);
    }
  };

  return (
    <div className={css.ctnAdvanceSearch}>
      <EmptyResultsModal show={isEmpty} onClose={() => setIsEmpty(false)} />

      <Box className={css.ctnAdvanceSearch_boxContent}>
        <AdvancedSelectorsWrapper
          className={css.hasBottomSeparation}
          scenario={scenario}
          onChangeState={setSelectorsState}
        />
      </Box>

      {loading ? (
        <Loading />
      ) : (
        <Button
          className={css.ctnAdvanceSearch_actionButton}
          onClick={() => callUpdateHook()}
          disabled={error || !hasSomeFilter}
        >
          {t("advanced-search.link.forms.btn-search")}
        </Button>
      )}
    </div>
  );
};

export default AdvanceSearch;
